import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Breadcrumb from '@/components/Breadcrumb';
import ProfileSideMenu from '@/components/ProfileSideMenu';
import useAuthStore from '@/stores/authStore';
import { logout } from '@/api/auth';

const MePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  
  // Determine active menu from current path
  const getActiveMenu = () => {
    const path = location.pathname;
    if (path === '/me') return 'profile';
    const segment = path.split('/')[2]; // Get the part after /me/
    return segment || 'profile';
  };
  
  const activeMenu = getActiveMenu();
  
  // Handle menu changes by navigating to the appropriate route
  const handleMenuChange = (menuId) => {
    if (menuId === 'logout') {
      // Call logout API (ignore result)
      logout().finally(() => {
        // Invalidate all queries in the cache
        queryClient.clear();

        // Clear authentication state
        clearAuth();

        // Navigate to home page
        navigate('/');
      });
      return;
    }

    if (menuId === 'feedback') {
      navigate('/contact');
      return;
    }

    if (menuId === 'profile') {
      navigate('/me');
    } else {
      navigate(`/me/${menuId}`);
    }
  };

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    { label: t('me.title'), href: '/me' }
  ];
  
  // Add a third breadcrumb item if we're in a subsection (but not for feedback since it navigates away)
  if (activeMenu !== 'profile' && activeMenu !== 'feedback') {
    breadcrumbItems.push({
      label: t(`me.menu.${activeMenu}`)
    });
  }

  return (
    <div className="w-full bg-[#f7f7f7]">
      <div className="container py-6 flex flex-col gap-6">
        <Breadcrumb items={breadcrumbItems} />
        
        <div className="flex">
          <div className="w-64 flex-shrink-0">
            <ProfileSideMenu activeMenu={activeMenu} onMenuChange={handleMenuChange} />
          </div>
          
          <div className="min-h-[600px] flex-1 ml-6">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MePage; 